import { Link } from 'react-router-dom';
import Button from '../../../components/base/Button';

const HeroSection = () => {
  return (
    <section className="relative w-full min-h-[700px] overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/d369537d111a12c3e12dd37a044096ba.jpg"
          alt="Cybersecurity risk assessment background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
      </div>

      {/* Hero Content */}
      <div className="relative z-10 flex items-center min-h-[700px] py-16">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* Left Column — Messaging */}
            <div className="w-full">
              {/* Urgency Badge */}
              <div className="inline-flex items-center gap-2 bg-red-600/20 border border-red-500/40 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                </span>
                <span className="text-red-300 text-sm font-semibold tracking-wide uppercase">State Exam Coming? Act Now</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Compliance Starts With a
                <span className="text-red-500 block mt-1">Risk Assessment</span>
              </h1>

              <p className="text-lg text-gray-300 mb-4 max-w-xl leading-relaxed">
                Before you can fix your security gaps, you need to find them. Our cybersecurity risk assessment is the critical first step to protecting your firm and passing your next state examination.
              </p>

              <p className="text-base text-gray-400 mb-8 max-w-xl">
                Get a comprehensive vulnerability scan, compliance gap analysis, and a prioritized action plan — delivered in 7 days or less.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <Link to="/purchase/risk-assessment" className="w-full sm:w-auto">
                  <Button 
                    variant="primary" 
                    size="lg"
                    className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 text-lg font-bold whitespace-nowrap w-full shadow-lg shadow-red-600/30 hover:shadow-red-600/50 transition-all duration-300"
                  >
                    <i className="ri-shield-check-line mr-2 text-xl"></i>
                    Get Your Risk Assessment — $250
                  </Button>
                </Link>
                <Link to="/schedule-call" className="w-full sm:w-auto">
                  <Button 
                    variant="outline" 
                    size="lg"
                    className="border-2 border-white/30 text-white bg-white/5 hover:bg-white/15 hover:border-white/50 px-8 py-4 text-lg rounded-xl font-semibold whitespace-nowrap w-full backdrop-blur-sm transition-all duration-300"
                  >
                    Schedule a Call
                  </Button>
                </Link>
              </div>

              {/* Value Props */}
              <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm text-gray-300">
                <div className="flex items-center gap-2">
                  <i className="ri-check-double-line text-green-400 text-lg"></i>
                  <span>Full Compliance Gap Analysis</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-check-double-line text-green-400 text-lg"></i>
                  <span>Results in 7 Days or Less</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-check-double-line text-green-400 text-lg"></i>
                  <span>100% Satisfaction Guarantee</span>
                </div>
              </div>
            </div>

            {/* Right Column — Assessment Preview Card */}
            <div className="hidden lg:block w-full">
              <div className="relative">
                {/* Main Card */}
                <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl p-8 shadow-2xl">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 flex items-center justify-center bg-red-600 rounded-lg">
                      <i className="ri-file-search-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg">Security Risk Assessment</h3>
                      <p className="text-gray-400 text-sm">What you&apos;ll receive</p>
                    </div>
                  </div>

                  <div className="space-y-4 mb-8">
                    {[
                      { icon: 'ri-radar-line', text: 'Complete Network Vulnerability Scan' },
                      { icon: 'ri-file-list-3-line', text: 'Security Policy Review' },
                      { icon: 'ri-user-search-line', text: 'Employee Security Awareness Check' },
                      { icon: 'ri-scales-3-line', text: 'GLBA & State Compliance Gap Analysis' },
                      { icon: 'ri-bar-chart-box-line', text: 'Detailed Risk Report & Recommendations' },
                      { icon: 'ri-presentation-line', text: 'Executive Summary for Leadership' },
                      { icon: 'ri-phone-line', text: '30-Minute Results Review Call' },
                      { icon: 'ri-route-line', text: 'Prioritized Action Plan with Timeline' },
                    ].map((item, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg flex-shrink-0">
                          <i className={`${item.icon} text-red-400`}></i>
                        </div>
                        <span className="text-gray-200 text-sm">{item.text}</span>
                      </div>
                    ))}
                  </div>

                  {/* Price & CTA inside card */}
                  <div className="border-t border-white/10 pt-6">
                    <div className="flex items-baseline gap-3 mb-1">
                      <span className="text-3xl font-bold text-white">$250</span>
                      <span className="text-lg text-gray-500 line-through">$499</span>
                      <span className="bg-green-500/20 text-green-400 text-xs font-bold px-2 py-1 rounded-full">SAVE 50%</span>
                    </div>
                    <p className="text-gray-400 text-sm mb-5">One-time payment • No hidden fees</p>
                    <Link to="/purchase/risk-assessment" className="block">
                      <button className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3.5 rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg shadow-red-600/20 hover:shadow-red-600/40">
                        Purchase Now
                        <i className="ri-arrow-right-line ml-2"></i>
                      </button>
                    </Link>
                  </div>
                </div>

                {/* Floating Stats */}
                <div className="absolute -bottom-6 -left-6 bg-white/10 backdrop-blur-md border border-white/15 rounded-xl px-5 py-4 shadow-xl">
                  <div className="text-2xl font-bold text-green-400">500+</div>
                  <div className="text-xs text-gray-300">Assessments Completed</div>
                </div>

                <div className="absolute -top-4 -right-4 bg-white/10 backdrop-blur-md border border-white/15 rounded-xl px-5 py-4 shadow-xl">
                  <div className="text-2xl font-bold text-yellow-400">100%</div>
                  <div className="text-xs text-gray-300">Satisfaction Guarantee</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
